import { create } from "zustand";
import { persist } from "zustand/middleware";

// ===== Types =====
export interface SearchState {
  // Global Search (Conversations)
  globalSearchQuery: string;
  globalSearchHistory: string[];
  isGlobalSearchOpen: boolean;

  // In-Chat Search (Messages)
  inChatSearchQuery: string;
  inChatSearchResults: number[];
  currentInChatResult: number;
  isInChatSearchActive: boolean;
}

export interface SearchActions {
  // Global Search Actions
  setGlobalSearchQuery: (query: string) => void;
  setGlobalSearchOpen: (open: boolean) => void;
  addToGlobalSearchHistory: (query: string) => void;
  clearGlobalSearchHistory: () => void;

  // In-Chat Search Actions
  setInChatSearchQuery: (query: string) => void;
  setInChatSearchResults: (results: number[]) => void;
  setCurrentInChatResult: (result: number) => void;
  setInChatSearchActive: (active: boolean) => void;
  clearInChatSearch: () => void;

  // Utils
  resetAllSearch: () => void;
}

export type SearchStore = SearchState & SearchActions;

// ===== Initial State =====
const initialState: SearchState = {
  // Global Search
  globalSearchQuery: "",
  globalSearchHistory: [],
  isGlobalSearchOpen: false,

  // In-Chat Search
  inChatSearchQuery: "",
  inChatSearchResults: [],
  currentInChatResult: 0,
  isInChatSearchActive: false,
};

// ===== Store Implementation =====
export const useSearchStore = create<SearchStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ===== Global Search Actions =====
      setGlobalSearchQuery: (query: string) =>
        set({ globalSearchQuery: query }),

      setGlobalSearchOpen: (open: boolean) => set({ isGlobalSearchOpen: open }),

      addToGlobalSearchHistory: (query: string) => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) return;

        const { globalSearchHistory } = get();
        const newHistory = [
          trimmedQuery,
          ...globalSearchHistory.filter((q) => q !== trimmedQuery),
        ].slice(0, 10); // Keep max 10 items

        set({ globalSearchHistory: newHistory });
      },

      clearGlobalSearchHistory: () => set({ globalSearchHistory: [] }),

      // ===== In-Chat Search Actions =====
      setInChatSearchQuery: (query: string) =>
        set({ inChatSearchQuery: query }),

      setInChatSearchResults: (results: number[]) =>
        set({
          inChatSearchResults: results,
          currentInChatResult: results.length > 0 ? 0 : 0,
        }),

      setCurrentInChatResult: (result: number) =>
        set({ currentInChatResult: result }),

      setInChatSearchActive: (active: boolean) =>
        set({
          isInChatSearchActive: active,
          ...(active
            ? {}
            : {
                inChatSearchQuery: "",
                inChatSearchResults: [],
                currentInChatResult: 0,
              }),
        }),

      clearInChatSearch: () =>
        set({
          inChatSearchQuery: "",
          inChatSearchResults: [],
          currentInChatResult: 0,
          isInChatSearchActive: false,
        }),

      // ===== Utils =====
      resetAllSearch: () =>
        set({
          ...initialState,
          globalSearchHistory: get().globalSearchHistory, // Keep history
        }),
    }),
    {
      name: "harmonyai-search-store",
      partialize: (state) => ({
        globalSearchHistory: state.globalSearchHistory,
      }),
    }
  )
);
